import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHfBranch } from "./useHfBranch";
import {
  createClinicalNotes,
  getClinicalNotes,
  getOneClinicalNote,
} from "service";
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/*
{
  "count": 123,
  "next": "http://api.example.org/accounts/?page=4",
  "previous": "http://api.example.org/accounts/?page=2",
  "results": [
    {
      "id": 0,
      "user": {
        "id": 0,
        "ino_id": "string",
        "name": "string",
        "image": "string",
        "email": "user@example.com"
      },
      "hf_personnel": "string",
      "created_on": "2025-06-02T09:38:38.147Z"
      "observations": "string"
    }
  ]
}
 */

export interface IClinicalNotesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IClinicalNotesResult[];
}

export interface IClinicalNotesResult {
  id: number;
  user?: {
    id: number;
    ino_id: string;
    name: string;
    image: string;
    email: string;
  };
  hf_personnel: string;
  created_on: string;
  observation?: string;
}

export function useClinicalNotes(noteId?: number) {
  const queryClient = useQueryClient();
  const { branch } = useHfBranch();
  const [formData, setFormData] = useState({
    client_ino: "",
    observation: "",
  });
  const navigate = useNavigate();

  const handleFieldChange = (
    field: keyof typeof formData,
    value: string | number | boolean
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  //! get clinical notes
  const {
    data: clinicalNotes,
    isPending: clinicalNotesLoading,
    error: clinicalNotesError,
  } = useQuery<IClinicalNotesResponse>({
    queryKey: ["clinical-notes", branch?.hfId],
    queryFn: async () => {
      if (!branch?.hfId) {
        throw new Error("No health facility selected");
      }
      return getClinicalNotes(branch.hfId);
    },
    enabled: !!branch?.hfId,
  });

  //! add clinical notes
  const { mutate: addClinicalNotes, isPending: addClinicalNotesLoading } =
    useMutation({
      mutationFn: async (formData: {
        client_ino: string;
        observation: string;
      }) => {
        if (!branch?.hfId) {
          throw new Error("No health facility selected");
        }
        return createClinicalNotes(branch.hfId, formData);
      },
      onSuccess: async (data) => {
        toast.success("Clinical notes added successfully");
        setFormData({
          client_ino: "",
          observation: "",
        });
        await queryClient.invalidateQueries({
          queryKey: ["clinical-notes", branch?.hfId],
        });
        navigate(-1);
      },
      onError: (error: any) => {
        const errors = error.response?.data.errors;
        if (Array.isArray(errors)) {
          const errorMessages = errors.map((err) => err.detail).join(", ");
          toast.error(errorMessages);
        } else {
          toast.error("An unknown error occurred.");
        }
      },
    });

  //! get one clinical notes
  const {
    data: clinicalNote,
    isPending: clinicalNoteLoading,
    error: clinicalNoteError,
  } = useQuery<IClinicalNotesResult>({
    queryKey: ["clinical-note", branch?.hfId, noteId],
    queryFn: async () => {
      if (!branch?.hfId || !noteId) {
        throw new Error("No health facility selected");
      }
      return getOneClinicalNote(branch.hfId, noteId);
    },
    enabled: !!branch?.hfId && !!noteId,
  });

  return {
    navigate,
    clinicalNotes,
    isLoading: clinicalNotesLoading,
    error: clinicalNotesError,
    addClinicalNotes,
    addClinicalNotesLoading,
    formData,
    handleFieldChange,
    clinicalNote,
    clinicalNoteLoading,
    clinicalNoteError,
  };
}
